import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';

/**
 * U10 inspection reminders for the Fleet dashboard.
 *
 * Two sections:
 *   - Due now: 5+ months since the last inspection, not yet acknowledged.
 *   - Coming up: due in the next 30 days, not yet acknowledged.
 *
 * The whole panel hides when both sections are empty. Acknowledge = "I have
 * scheduled the next inspection." Acknowledged rows leave both sections.
 *
 * @param {Object} props
 * @param {string} props.API_BASE
 * @param {string} props.token
 */
export default function U10RemindersPanel({ API_BASE, token }) {
  const [due, setDue] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [confirm, setConfirm] = useState({ open: false, row: null });
  const [acknowledging, setAcknowledging] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/reminders/due`, auth);
      setDue(data?.due || []);
      setUpcoming(data?.upcoming || []);
    } catch (err) {
      toast.error('Failed to load safety inspection reminders');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, token]);

  useEffect(() => { load(); }, [load]);

  const askAcknowledge = (row) => setConfirm({ open: true, row });
  const cancelAcknowledge = () => setConfirm({ open: false, row: null });

  const doAcknowledge = async () => {
    if (!confirm.row) return;
    setAcknowledging(true);
    try {
      await axios.patch(
        `${API_BASE}/api/reminders/${confirm.row.ServiceId}/acknowledge`,
        {},
        auth,
      );
      setDue((rs) => rs.filter((r) => r.ServiceId !== confirm.row.ServiceId));
      setUpcoming((rs) => rs.filter((r) => r.ServiceId !== confirm.row.ServiceId));
      toast.success('Marked as scheduled');
      cancelAcknowledge();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to acknowledge reminder');
    } finally {
      setAcknowledging(false);
    }
  };

  if (loading) return null;
  if (due.length === 0 && upcoming.length === 0) return null;

  // If anything is genuinely due, lean on the alert styling. Otherwise, the
  // panel exists only for the heads-up "coming soon" list, so dial it back.
  const isAlertMode = due.length > 0;

  return (
    <>
      <Card
        sx={{
          mb: 2,
          borderLeft: 4,
          borderColor: isAlertMode ? 'warning.main' : 'info.main',
          bgcolor: isAlertMode ? 'warning.50' : 'info.50',
        }}
      >
        <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              {isAlertMode
                ? <WarningAmberIcon color="warning" aria-hidden="true" />
                : <EventIcon color="info" aria-hidden="true" />}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Safety Inspections
              </Typography>
              {due.length > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningAmberIcon fontSize="small" aria-hidden="true" />}
                  label={`${due.length} due now`}
                  aria-label={`${due.length} inspections due now`}
                />
              )}
              {upcoming.length > 0 && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<EventIcon fontSize="small" aria-hidden="true" />}
                  label={`${upcoming.length} coming up in 30 days`}
                  aria-label={`${upcoming.length} inspections coming up in the next 30 days`}
                />
              )}
            </Stack>
            <IconButton
              size="small"
              onClick={() => setExpanded((e) => !e)}
              aria-label={expanded ? 'Collapse safety inspections panel' : 'Expand safety inspections panel'}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          <Collapse in={expanded} timeout="auto">
            <Stack spacing={2} sx={{ mt: 2 }}>
              {due.length > 0 && (
                <Section
                  title="Due now"
                  rows={due}
                  variant="due"
                  onAck={askAcknowledge}
                />
              )}

              {due.length > 0 && upcoming.length > 0 && <Divider />}

              {upcoming.length > 0 && (
                <Section
                  title="Coming up in the next 30 days"
                  rows={upcoming}
                  variant="upcoming"
                  onAck={askAcknowledge}
                />
              )}
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      <Dialog open={confirm.open} onClose={cancelAcknowledge} maxWidth="xs" fullWidth>
        <DialogTitle>Mark inspection as scheduled?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm that the Semi Annual Safety U10 inspection for{' '}
            <strong>{vehicleLabel(confirm.row)}</strong> has been scheduled.
            This removes it from both Due now and Coming up.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelAcknowledge} disabled={acknowledging}>Cancel</Button>
          <Button
            onClick={doAcknowledge}
            color="warning"
            variant="contained"
            disabled={acknowledging}
            startIcon={acknowledging ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Mark as scheduled
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Section({ title, rows, variant, onAck }) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}
      >
        {title}
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {rows.map((row) => (
          <ReminderRow
            key={row.ServiceId}
            row={row}
            variant={variant}
            onAck={() => onAck(row)}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * A single reminder row. `variant` controls the time chip:
 *   - "due"      → red/yellow "N days overdue" / "Due today"
 *   - "upcoming" → blue "Due in N days"
 */
function ReminderRow({ row, variant, onAck }) {
  const chip = variant === 'due'
    ? overdueChip(row.DaysOverdue)
    : upcomingChip(row.DaysUntilDue);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.25,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {vehicleLabel(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Last inspected {fmtDate(row.ServiceDate)} - due {fmtDate(row.ReminderDueDate)}
        </Typography>
      </Box>
      {chip}
      <Button size="small" variant="outlined" onClick={onAck}>
        Mark as scheduled
      </Button>
    </Box>
  );
}

function overdueChip(daysOverdue) {
  const n = Number(daysOverdue || 0);
  const label = n > 0 ? `${n} days overdue` : 'Due today';
  return (
    <Chip
      size="small"
      color={n > 30 ? 'error' : 'warning'}
      icon={<WarningAmberIcon fontSize="small" aria-hidden="true" />}
      label={label}
      aria-label={`Status: ${label}`}
    />
  );
}

function upcomingChip(daysUntilDue) {
  const n = Number(daysUntilDue || 0);
  const label = n <= 0 ? 'Due today' : `Due in ${n} day${n === 1 ? '' : 's'}`;
  return (
    <Chip
      size="small"
      color="info"
      variant="outlined"
      icon={<EventIcon fontSize="small" aria-hidden="true" />}
      label={label}
      aria-label={`Status: ${label}`}
    />
  );
}

function vehicleLabel(row) {
  if (!row) return '';
  const plate = row.License ? ` (${row.License})` : '';
  const mmy = [row.Year, row.Make, row.Model].filter(Boolean).join(' ');
  return `${mmy}${plate}`.trim() || `Vehicle #${row.VehicleId}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}
